import { Component, PropsWithChildren, useEffect } from "react";

import { Dialog } from "./Dialog";
import { StateContext } from "./state/StateProvider";
import * as svg from "./svg";
import i18n, { resources } from "../i18n";
import { useLamp } from "../Lamp";
import { Tracker } from "../track";

const ErrorDialog = () => {
    i18n.init({ resources });
    const lang = localStorage.getItem("language");
    const t = i18n.getFixedT(lang ?? "de", "translation");

    const { setColor } = useLamp();
    setColor("Red");

    useEffect(() => {
        const timeout = setTimeout(() => window.location.reload(), 10000);
        return () => clearTimeout(timeout);
    }, []);

    return (
        <Dialog
            dataTestId="error-dialog"
            image={svg.productNotFound}
            title={t("somethingWentWrong")}
            primaryButton={{
                title: t("reload"),
                action: () => {
                    setColor("Green");
                    window.location.reload();
                }
            }}
        />
    );
};

class ErrorBoundary extends Component<
    PropsWithChildren,
    { hasError: boolean }
> {
    static contextType = StateContext;

    constructor(props: PropsWithChildren) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error) {
        new Tracker().track("exception", {
            description: error.message,
            stack: error.stack,
            fatal: false
        });
        return { hasError: true };
    }

    private promiseRejectionHandler = (_: PromiseRejectionEvent) => {
        this.setState({ hasError: true });
    };

    componentDidMount() {
        // Add an event listener to the window to catch unhandled promise rejections & stash the error in the state
        window.addEventListener(
            "unhandledrejection",
            this.promiseRejectionHandler
        );
    }

    componentWillUnmount() {
        window.removeEventListener(
            "unhandledrejection",
            this.promiseRejectionHandler
        );
    }

    public render() {
        if (this.state.hasError) {
            return <ErrorDialog />;
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
